import { memo } from 'react'
import type { NodeProps } from '@xyflow/react'
import { Handle, Position } from '@xyflow/react'
import { VECTOR_COLOURS, type VectorDetail, type VectorStyle } from './vectorize'
import { FAL_VECTOR_CREDITS } from '../../shared/services/vectorizeService'
import '../../shared/components/upscale.css'
import { SkillGlyph } from '@shared/components/SkillGlyph'

/**
 * The Vectorize card (Huanrui, 2026-10-09)
 * -------------------------------------------------------------
 * One picture wired in on the left; an SVG of it comes out on the right,
 * traced in this browser (vectorize.ts). Free: no model, no server work
 * beyond keeping the file. On the board only.
 *
 * Or, paid, by Recraft through fal (Huanrui, 2026-10-09): cleaner on busy
 * illustrations, no settings, about 40 credits (routes/vectorize.ts).
 * While either works, a frame with the loader waits on the board.
 */

export type VectorEngine = 'free' | 'fal'

export interface VectorNodeData extends Record<string, unknown> {
  skillRef: string
  name: string
  glyph: string
  glow: string
  open?: boolean
  engine?: VectorEngine
  style?: VectorStyle
  colours?: number
  detail?: VectorDetail
  /** Being traced now. */
  running?: boolean
  error?: string

  /* handed in by the board, never stored */
  wired?: boolean
  onEdit?: (patch: Partial<VectorNodeData>) => void
  onRun?: () => void
}

const STYLES: { value: VectorStyle; label: string }[] = [
  { value: 'colour', label: 'Colour' },
  { value: 'mono', label: 'Black & white' },
]

const DETAILS: { value: VectorDetail; label: string }[] = [
  { value: 'smooth', label: 'Smooth' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'sharp', label: 'Sharp' },
]

export const VectorNode = memo(function VectorNode({ data, selected }: NodeProps) {
  const card = data as VectorNodeData
  const isOpen = card.open !== false
  const running = card.running === true
  const wired = card.wired === true
  const engine = card.engine ?? 'free'
  const style = card.style ?? 'colour'
  const colours = card.colours ?? 8
  const detail = card.detail ?? 'balanced'
  const edit = (patch: Partial<VectorNodeData>) => card.onEdit?.(patch)

  return (
    <div
      className={[
        'cnode',
        'pnode',
        'xnode',
        isOpen ? 'pnode--open' : '',
        selected ? 'cnode--selected' : '',
        wired ? 'cnode--lit' : '',
        running ? 'cnode--running' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--tile-glow': card.glow } as React.CSSProperties}
    >
      <Handle type="target" id="in" position={Position.Left} className="cnode__port" />
      <Handle type="source" id="out" position={Position.Right} className="cnode__port" />

      <div className="pnode__head">
        <span className="cnode__glyph" aria-hidden="true">
          <SkillGlyph glyph={card.glyph} />
        </span>
        <span className="cnode__name">{card.name}</span>
        <button
          type="button"
          className="pnode__toggle nodrag"
          onClick={() => edit({ open: !isOpen })}
          title={isOpen ? 'Fold this card' : 'Unfold this card'}
          aria-label={isOpen ? 'Fold this card' : 'Unfold this card'}
        >
          {isOpen ? '−' : '+'}
        </button>
      </div>

      {isOpen ? (
        <div className="pnode__body">
          {/* The paid way as a switch (Huanrui, 2026-10-09): off is the free tracing. */}
          <label className="wwup__switch nodrag">
            <input
              type="checkbox"
              checked={engine === 'fal'}
              onChange={(event) => edit({ engine: event.target.checked ? 'fal' : 'free' })}
            />
            <span className="wwup__track" aria-hidden="true" />
            <span className="wwup__switch-text">
              Use AI (Recraft · fal)
              <span className="wwup__switch-sub">
                {engine === 'fal'
                  ? `Cleaner shapes from busy illustrations. About ${FAL_VECTOR_CREDITS} credits.`
                  : 'Off: traced free in this browser, with the settings below.'}
              </span>
            </span>
          </label>

          {engine === 'free' ? (
          <>
          <div className="pnode__field">
            <span className="pnode__label">Style</span>
            <div className="pnode__chips pnode__chips--grid">
              {STYLES.map((one) => (
                <button
                  key={one.value}
                  type="button"
                  className={`pnode__chip nodrag${one.value === style ? ' pnode__chip--on' : ''}`}
                  onClick={() => edit({ style: one.value })}
                >
                  {one.label}
                </button>
              ))}
            </div>
          </div>

          {style === 'colour' ? (
            <div className="pnode__field">
              <span className="pnode__label">Colours</span>
              <div className="pnode__chips pnode__chips--grid">
                {VECTOR_COLOURS.map((one) => (
                  <button
                    key={one}
                    type="button"
                    className={`pnode__chip nodrag${one === colours ? ' pnode__chip--on' : ''}`}
                    onClick={() => edit({ colours: one })}
                  >
                    {one}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="pnode__field">
            <span className="pnode__label">Detail</span>
            <div className="pnode__chips pnode__chips--grid">
              {DETAILS.map((one) => (
                <button
                  key={one.value}
                  type="button"
                  className={`pnode__chip nodrag${one.value === detail ? ' pnode__chip--on' : ''}`}
                  onClick={() => edit({ detail: one.value })}
                >
                  {one.label}
                </button>
              ))}
            </div>
          </div>

          </>
          ) : null}

          {engine === 'free' ? <p className="xnode__hint">Best for logos, line art and flat graphics.</p> : null}
          {card.error ? <p className="pnode__error">{card.error}</p> : null}
        </div>
      ) : null}

      {isOpen || running ? (
        <button
          type="button"
          className="cnode__run pnode__run nodrag"
          onClick={() => card.onRun?.()}
          disabled={running || !wired}
          title={!wired ? 'Connect a picture first' : 'Trace the picture into an SVG'}
        >
          {running ? (
            'Vectorizing…'
          ) : (
            <>
              Vectorize
              <span className="pnode__credits">{engine === 'fal' ? `about ${FAL_VECTOR_CREDITS} credits` : 'free'}</span>
            </>
          )}
        </button>
      ) : null}

      {!isOpen && card.error ? <span className="cnode__error">{card.error}</span> : null}
    </div>
  )
})
